import React from "react";
import { useTranslation } from "react-i18next";
import styles from "../CRMSettingsPage.module.css";

function HelpHint({ title, text }) {
  return (
    <span
      title={text}
      aria-label={title}
      style={{
        marginLeft: 6,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: "999px",
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(255,255,255,0.05)",
        color: "rgba(255,255,255,0.72)",
        fontSize: 10,
        fontWeight: 900,
        cursor: "help",
        userSelect: "none",
        verticalAlign: "middle",
      }}
    >
      ?
    </span>
  );
}

export default function GoogleTab({
  googleEnabled,
  setGoogleEnabled,
  googleClientId,
  setGoogleClientId,
  googleClientSecret,
  setGoogleClientSecret,
  googleRedirectUri,
  setGoogleRedirectUri,
  googleFrontendSuccessUrl,
  setGoogleFrontendSuccessUrl,
  googleFrontendErrorUrl,
  setGoogleFrontendErrorUrl,
  googleDriveStatus,
  googleDriveLoading,
  onConnectGoogleDrive,
  onRefreshGoogleDriveStatus,
  onDisconnectGoogleDrive,
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.emailShell}>
      <div className={styles.emailHeader}>
        <div>
          <div
            className={styles.emailTitle}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            {t("crm_settings_tab_google")}
            <HelpHint
              title={t("crm_settings_google_oauth_title")}
              text={t("crm_settings_google_oauth_help")}
            />
          </div>

          <div className={styles.emailHint}>
            {t("crm_settings_google_oauth_subtitle")}
          </div>
        </div>
      </div>

      <div className={styles.emailGrid2}>
        <div className={styles.emailCard}>
          <div className={styles.emailCardHead}>
            <div className={styles.emailCardTitle}>
              {t("crm_settings_google_oauth_general")}
            </div>
            <span className={styles.pill}>{t("crm_settings_tenant")}</span>
          </div>

          <div className={styles.formCol}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_google_enabled")}
                </div>
                <div className={styles.fHelp}>
                  {t("crm_settings_google_enabled_hint")}
                </div>
              </div>

              <div className={styles.toggle}>
                <div className={styles.toggleLeft}>
                  <input
                    type="checkbox"
                    checked={googleEnabled}
                    onChange={(e) => setGoogleEnabled(e.target.checked)}
                  />
                  <span
                    className={styles.fLabel}
                    style={{ opacity: 0.9, fontWeight: 700 }}
                  >
                    {googleEnabled
                      ? t("crm_settings_on")
                      : t("crm_settings_off")}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_google_client_id")}
                </div>
              </div>
              <input
                className={styles.ctrl}
                value={googleClientId}
                onChange={(e) => setGoogleClientId(e.target.value)}
                placeholder={t("crm_settings_google_client_id_placeholder")}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_google_client_secret")}
                </div>
                <div className={styles.fHelp}>
                  {t("crm_settings_secret_masked_hint")}
                </div>
              </div>
              <input
                className={styles.ctrl}
                type="password"
                value={googleClientSecret}
                onChange={(e) => setGoogleClientSecret(e.target.value)}
                placeholder={t("crm_settings_google_client_secret_placeholder")}
              />
            </div>
          </div>
        </div>

        <div className={styles.emailCard}>
          <div className={styles.emailCardHead}>
            <div className={styles.emailCardTitle}>
              {t("crm_settings_google_oauth_urls")}
            </div>
            <span className={styles.pill}>
              {t("crm_settings_redirects")}
            </span>
          </div>

          <div className={styles.formCol}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_google_redirect_uri")}
                </div>
              </div>
              <input
                className={styles.ctrl}
                value={googleRedirectUri}
                onChange={(e) => setGoogleRedirectUri(e.target.value)}
                placeholder={t("crm_settings_google_redirect_uri_placeholder")}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_google_frontend_success_url")}
                </div>
              </div>
              <input
                className={styles.ctrl}
                value={googleFrontendSuccessUrl}
                onChange={(e) => setGoogleFrontendSuccessUrl(e.target.value)}
                placeholder={t("crm_settings_google_frontend_success_url_placeholder")}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_google_frontend_error_url")}
                </div>
              </div>
              <input
                className={styles.ctrl}
                value={googleFrontendErrorUrl}
                onChange={(e) => setGoogleFrontendErrorUrl(e.target.value)}
                placeholder={t("crm_settings_google_frontend_error_url_placeholder")}
              />
            </div>

            <div
              className={styles.cardSub}
              style={{
                marginTop: 6,
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)",
              }}
            >
              {t("crm_settings_google_oauth_note")}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.emailCard} style={{ marginTop: 16 }}>
        <div className={styles.emailCardHead}>
          <div>
            <div className={styles.emailCardTitle}>{t("google_drive_photo_storage")}</div>
            <div className={styles.cardSub}>
              {t("google_drive_photo_storage_description_exact")}
            </div>
          </div>
          <span className={styles.pill}>{t("google_drive_per_tenant")}</span>
        </div>

        <div className={styles.formCol}>
          <div className={styles.field}>
            <div className={styles.labelRow}>
              <div className={styles.fLabel}>{t("google_drive_connection_status")}</div>
              <div className={styles.fHelp}>
                {googleDriveStatus?.connected
                  ? `Connected: ${googleDriveStatus?.connected_email || "Google Drive"}`
                  : "Not connected yet"}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <button
                type="button"
                className={styles.primaryAction}
                onClick={onConnectGoogleDrive}
                disabled={googleDriveLoading}
              >
                {googleDriveStatus?.connected ? "Reconnect Google Drive" : "Connect Google Drive"}
              </button>

              <button
                type="button"
                className={styles.secondaryAction}
                onClick={onRefreshGoogleDriveStatus}
                disabled={googleDriveLoading}
              >
                {t("google_drive_refresh_status")}
              </button>

              {googleDriveStatus?.connected && (
                <button
                  type="button"
                  className={styles.secondaryAction}
                  onClick={onDisconnectGoogleDrive}
                  disabled={googleDriveLoading}
                >
                  {t("google_drive_disconnect")}
                </button>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.labelRow}>
              <div className={styles.fLabel}>{t("google_drive_folder")}</div>
              <div className={styles.fHelp}>
                {googleDriveStatus?.root_folder_name || "VelSoft CRM Photos"}
                {googleDriveStatus?.root_folder_id ? ` / ${googleDriveStatus.root_folder_id}` : ""}
              </div>
            </div>
            <div className={styles.cardSub}>
              {t("google_drive_connect_hint")}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}